import axios from "axios";
import { API_BASE_URL } from "../config/apiConfig";

// Cliente axios central do frontend: injeta o JWT (Authorization: Bearer) em toda requisição.
// Centralizar aqui garante que, ao exigir autenticação no backend, todas as chamadas levem o token.
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("clinic4us-token");
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Subdominios que NAO sao clinica (RN-001c). Sao os hosts da propria plataforma; sem esta lista,
 * abrir `app.clinic4us.com.br` seria lido como a clinica "app" e o login procuraria um tenant
 * que nao existe.
 */
const HOSTS_DA_PLATAFORMA = new Set([
  "app", "api", "www", "coolify", "legado", "admin",
  "hml-app", "hml-api", "hml-admin", "homolog", "localhost",
]);

/**
 * Prefixo do ambiente de HOMOLOGACAO no endereco da clinica: `hml-ninho.clinic4us.com.br` e a
 * mesma clinica `ninho`, em outro ambiente.
 *
 * Existe por uma limitacao do certificado: o curinga da Let's Encrypt cobre UM nivel de
 * subdominio, entao `*.clinic4us.com.br` vale para `hml-ninho` mas NAO valeria para
 * `ninho.hml.clinic4us.com.br` -- essa forma exigiria um segundo certificado curinga. O prefixo
 * foi o jeito de homologacao ter endereco por cliente reusando o mesmo certificado.
 */
const PREFIXO_HOMOLOGACAO = "hml-";

/**
 * Alias vindo do SUBDOMINIO (`ninho.clinic4us.com.br` -> "ninho"). Devolve vazio quando o host
 * nao e um subdominio de clinica: dominio de plataforma, IP, localhost ou dominio sem o nivel
 * extra. E a fonte de MAIOR prioridade porque e a mais explicita -- quem digitou o endereco da
 * clinica esta dizendo em qual quer entrar.
 */
export const aliasFromHost = (): string => {
  if (typeof window === "undefined") return "";
  const host = window.location.hostname.toLowerCase();

  // IP puro ou host sem ponto (localhost) nao carregam alias.
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host) || !host.includes(".")) return "";

  const partes = host.split(".");
  // Precisa de pelo menos um nivel ALEM do dominio (clinic4us.com.br = 3 partes).
  if (partes.length < 4) return "";

  const primeiro = partes[0];
  if (HOSTS_DA_PLATAFORMA.has(primeiro)) return "";

  // `hml-ninho` e a clinica `ninho` em homologacao; sem tirar o prefixo, o login procuraria um
  // tenant chamado "hml-ninho", que nao existe em central nenhuma.
  return primeiro.startsWith(PREFIXO_HOMOLOGACAO)
    ? primeiro.slice(PREFIXO_HOMOLOGACAO.length)
    : primeiro;
};

// Resolve o alias do tenant para reconstruir a URL de login (/<alias>).
// Prioridade: SUBDOMINIO (o mais explicito) -> sessionStorage (entrada por /<alias>, sobrevive
// ao logout) -> sessão persistida -> ?clinic= na URL.
export const resolveTenantAlias = (): string => {
  if (typeof window === "undefined") {
    return "";
  }
  const fromHost = aliasFromHost();
  if (fromHost) {
    return fromHost;
  }
  const fromSession = sessionStorage.getItem("clinic4us-clinic-alias");
  if (fromSession) {
    return fromSession;
  }
  try {
    const raw = localStorage.getItem("clinic4us-user-session");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.clinic) return parsed.clinic;
      if (parsed?.alias) return parsed.alias;
    }
  } catch {
    // sessão inválida — ignora e cai para o ?clinic=
  }
  return new URLSearchParams(window.location.search).get("clinic") || "";
};

// Redireciona para o login mantendo o alias no path (ex.: /Ninho). O index.tsx, ao ver /<alias>,
// guarda o alias e normaliza para a página de login.
export const redirectToLogin = (): void => {
  if (typeof window === "undefined") {
    return;
  }
  // Com a clinica no SUBDOMINIO, o alias ja esta no endereco: repeti-lo no caminho daria
  // `ninho.clinic4us.com.br/ninho`, feio e redundante.
  const alias = aliasFromHost() ? "" : resolveTenantAlias();
  const target = alias
    ? `${window.location.origin}/${encodeURIComponent(alias)}`
    : `${window.location.origin}/?page=login`;
  if (window.location.href !== target) {
    window.location.href = target;
  }
};

/**
 * Enquanto o diálogo de bloqueio está aberto, a sessão NÃO pode ser derrubada por um 401
 * atrasado (achado do Hilton, 08/09/2026: "faz a renovação e não loga; só depois do login
 * normal").
 *
 * O que acontecia: quando o token morre, várias chamadas da tela dão 401 quase juntas. Cada uma
 * chamava `forceLogin`, que apaga token e sessão do localStorage. O usuário digitava a senha, a
 * renovação gravava o token novo — e um 401 que ainda estava em voo apagava tudo de novo. Por
 * isso o login pela senha funcionava no servidor (35 renovações com 200 em prd) e mesmo assim a
 * pessoa continuava deslogada.
 */
let sessaoProtegida = false;

export const protegerSessao = (protegida: boolean): void => {
  sessaoProtegida = protegida;
};

// 401 (token ausente/expirado) -> derruba a sessão e volta para o login (com o alias no path).
// 403 (autenticado, mas sem permissão para o recurso) NÃO desloga: apenas propaga o erro
// para a UI tratar, evitando loop de redirect ao acessar algo fora do perfil.
export const forceLogin = (): void => {
  if (typeof window === "undefined" || sessaoProtegida) {
    return;
  }
  localStorage.removeItem("clinic4us-token");
  localStorage.removeItem("clinic4us-user-session");
  localStorage.removeItem("clinic4us-remember-me");

  redirectToLogin();
};

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      forceLogin();
    }
    // 403 do enforcement de troca obrigatória (RN-005): o servidor bloqueia quem ainda
    // não trocou a senha inicial. Leva à tela de troca em vez de deixar erro solto —
    // backstop caso algum request escape do bloqueio de navegação do frontend.
    if (
      error?.response?.status === 403 &&
      error?.response?.data?.code === "Users.MustChangePassword" &&
      typeof window !== "undefined" &&
      !window.location.search.includes("page=change-password")
    ) {
      window.location.href = `${window.location.origin}/?page=change-password`;
    }
    return Promise.reject(error);
  }
);
